import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, X, Search, Loader2, User, Landmark } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

const API = import.meta.env.VITE_API_URL || '/api/v1';

interface Conversa {
  id: string;
  alunoId: string;
  responsavelId: string;
  professorId: string | null;
  ultimoTexto: string | null;
  criadoEm: string;
  atualizadoEm: string;
  aluno: { id: string; nome: string };
  professor: { id: string; nome: string } | null;
  escola: { id: string; nome: string };
  unreadCount: number;
}

interface ProfessorVinculado {
  id: string;
  nome: string;
  materias: string[];
}

interface AlunoProfessores {
  aluno: { id: string; nome: string };
  professores: ProfessorVinculado[];
}

export function Conversas() {
  const navigate = useNavigate();
  const token = localStorage.getItem('ac_token');
  const user = useAuthStore((s) => s.user);
  const responsavelId = user?.responsavelId;

  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  // States for new chat
  const [alunosProfessores, setAlunosProfessores] = useState<AlunoProfessores[]>([]);
  const [selectedAlunoId, setSelectedAlunoId] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!responsavelId) return;
    loadConversas();
  }, [responsavelId]);

  async function loadConversas() {
    try {
      const res = await fetch(`${API}/mobile/responsaveis/${responsavelId}/conversas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setConversas(await res.json());
        setErrorMsg(null);
      } else {
        setErrorMsg('Não foi possível carregar as conversas.');
        window.dispatchEvent(new CustomEvent('ac:client-error', {
          detail: { context: 'Conversas.loadConversas', error: `HTTP ${res.status}` }
        }));
      }
    } catch (err: any) {
      setErrorMsg('Falha de conexão.');
      window.dispatchEvent(new CustomEvent('ac:client-error', {
        detail: { context: 'Conversas.loadConversas', error: err?.message || 'Erro de rede' }
      }));
    }
    setLoading(false);
  }

  async function handleOpenNewChat() {
    setShowNewChatModal(true);
    setLoadingContacts(true);
    try {
      const res = await fetch(`${API}/mobile/responsaveis/${responsavelId}/professores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAlunosProfessores(data);
        if (data.length > 0) {
          setSelectedAlunoId(data[0].aluno.id);
        }
      } else {
        window.dispatchEvent(new CustomEvent('ac:client-error', {
          detail: { context: 'Conversas.handleOpenNewChat', error: `HTTP ${res.status}` }
        }));
      }
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('ac:client-error', {
        detail: { context: 'Conversas.handleOpenNewChat', error: err?.message || 'Erro ao buscar contatos' }
      }));
    }
    setLoadingContacts(false);
  }

  async function handleCreateChat(alunoId: string, professorId: string | null) {
    try {
      const res = await fetch(`${API}/mobile/responsaveis/${responsavelId}/conversas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ alunoId, professorId }),
      });
      if (res.ok) {
        const data = await res.json();
        setShowNewChatModal(false);
        navigate(`/mobile/conversas/${data.id}`);
      } else {
        window.dispatchEvent(new CustomEvent('ac:client-error', {
          detail: { context: 'Conversas.handleCreateChat', error: `HTTP ${res.status}` }
        }));
      }
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('ac:client-error', {
        detail: { context: 'Conversas.handleCreateChat', error: err?.message || 'Erro ao criar conversa' }
      }));
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
      </div>
    );
  }

  const selectedAlunoData = alunosProfessores.find(ap => ap.aluno.id === selectedAlunoId);

  return (
    <div className="m-animate" style={{ position: 'relative', minHeight: 'calc(100vh - 120px)' }}>
      {/* Header Area */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>Conversas</h2>
          <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '2px 0 0 0' }}>Fale com os professores ou a secretaria</p>
        </div>
        <button
          onClick={handleOpenNewChat}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '8px 14px',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 10px rgba(124, 58, 237, 0.25)',
          }}
        >
          <Plus size={16} /> Novo
        </button>
      </div>

      {/* Conversations List */}
      {conversas.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {conversas.map((c) => {
            const isSecretaria = !c.professor;
            const title = isSecretaria ? 'Secretaria Escolar' : `Prof. ${c.professor?.nome}`;
            const subtitle = `Assunto: ${c.aluno.nome}`;
            const time = new Date(c.atualizadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

            return (
              <div
                key={c.id}
                onClick={() => navigate(`/mobile/conversas/${c.id}`)}
                className="m-card"
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  borderRadius: '16px',
                  border: c.unreadCount > 0 ? '1.5px solid #7c3aed' : '1px solid var(--border-color)',
                  background: 'var(--bg-card)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: isSecretaria ? '#e0f2fe' : '#ede9fe',
                      color: isSecretaria ? '#0284c7' : '#7c3aed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isSecretaria ? <Landmark size={20} /> : <User size={20} />}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {title}
                      </h4>
                      <span style={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0 }}>{time}</span>
                    </div>
                    <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', margin: '2px 0 4px 0' }}>
                      {subtitle}
                    </p>
                    <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.ultimoTexto || 'Nenhuma mensagem.'}
                    </p>
                  </div>
                </div>

                {c.unreadCount > 0 && (
                  <div
                    style={{
                      background: '#7c3aed',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      borderRadius: '10px',
                      padding: '2px 8px',
                      marginLeft: 10,
                      flexShrink: 0,
                    }}
                  >
                    {c.unreadCount}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="m-empty" style={{ marginTop: 60 }}>
          <div className="m-empty-icon" style={{ background: '#f3f4f6', color: '#9ca3af' }}>
            <MessageSquare size={24} />
          </div>
          <p style={{ fontSize: '0.88rem', color: '#64748b', margin: '12px 0 0 0' }}>Nenhuma conversa ativa.</p>
          <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '4px 0 0 0' }}>Clique em "Novo" para iniciar um chat.</p>
        </div>
      )}

      {/* New Chat Modal / Bottom Sheet */}
      {showNewChatModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <div
            className="m-animate"
            style={{
              width: '100%',
              maxWidth: 480,
              background: 'white',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              padding: '20px 20px 30px 20px',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 -8px 24px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>Nova Conversa</h3>
              <button
                onClick={() => setShowNewChatModal(false)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <X size={16} style={{ color: '#64748b' }} />
              </button>
            </div>

            {loadingContacts ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
              </div>
            ) : (
              <div>
                {/* Select Child */}
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                  Selecione o Filho (Contexto)
                </label>
                <select
                  value={selectedAlunoId}
                  onChange={(e) => setSelectedAlunoId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    background: '#f8fafc',
                    color: '#1e293b',
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    marginBottom: 20,
                  }}
                >
                  {alunosProfessores.map((ap) => (
                    <option key={ap.aluno.id} value={ap.aluno.id}>
                      {ap.aluno.nome}
                    </option>
                  ))}
                </select>

                {/* Destination Options */}
                <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', marginBottom: 10, marginTop: 0 }}>
                  Falar com:
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Option: Secretaria */}
                  <div
                    onClick={() => handleCreateChat(selectedAlunoId, null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px',
                      borderRadius: '14px',
                      border: '1px solid var(--border-color)',
                      background: '#f8fafc',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
                  >
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#e0f2fe', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Landmark size={18} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Secretaria da Escola</h4>
                      <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '2px 0 0 0' }}>Dúvidas gerais, matrículas e financeiro</p>
                    </div>
                  </div>

                  {/* Options: Professors */}
                  {selectedAlunoData && selectedAlunoData.professores.length > 0 ? (
                    selectedAlunoData.professores.map((prof) => (
                      <div
                        key={prof.id}
                        onClick={() => handleCreateChat(selectedAlunoId, prof.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '14px',
                          borderRadius: '14px',
                          border: '1px solid var(--border-color)',
                          background: '#f8fafc',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
                      >
                        <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#ede9fe', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <User size={18} />
                        </div>
                        <div>
                          <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Prof. {prof.nome}</h4>
                          <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '2px 0 0 0' }}>
                            Matérias: {prof.materias.join(', ') || 'Nenhuma registrada'}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', padding: '10px 0', margin: 0 }}>
                      Nenhum professor vinculado a este aluno.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
